import { observable, computed, action } from 'mobx';
import {
  IContactApi,
  IContact,
  IEditContact,
} from 'common/interfaces/contacts';
import { app } from './../App';
import { Lead } from '.';

export enum EContactSavingError {
  None,
  NoName,
  Other,
}

export class LeadContacts {
  constructor(private lead: Lead) {}

  @observable public loading: boolean = false;
  @observable public errorLoading: boolean = false;

  @observable public providerId: number | null = null;
  @observable public all: IContact[] = [];
  @computed public get allIds(): number[] {
    return this.all.map((contact) => contact.id);
  }

  @computed public get primary(): IContact[] {
    return this.all.filter((contact) => {
      return contact.default === true;
    });
  }
  @computed public get secondary(): IContact[] {
    return this.all.filter((contact) => {
      return contact.default === false;
    });
  }
  @computed public get combined(): IContact[] {
    return [...this.primary, ...this.secondary];
  }

  @observable public creating: boolean = false;
  @observable public editing: boolean = false;
  @observable public saving: boolean = false;
  @observable public errorSaving: EContactSavingError =
    EContactSavingError.None;

  @observable public editedContacts: IEditContact[] = [];
  @computed public get editedContactsIds(): number[] {
    return this.editedContacts.map((contact) => contact.id);
  }

  @computed public get createContact(): IEditContact | null {
    let newContactIndex = null;

    this.editedContacts.some((contact, i) => {
      if (contact.id === null) {
        newContactIndex = i;
        return true;
      }

      return false;
    });

    return newContactIndex !== null
      ? this.editedContacts[newContactIndex]
      : null;
  }

  @action public fetchLeadContacts = async (
    providerId: number
  ): Promise<any> => {
    try {
      this.loading = true;
      this.errorLoading = false;
      this.editing = false;
      this.saving = false;
      this.errorSaving = EContactSavingError.None;
      this.providerId = providerId;
      this.all = [];

      const service = await app.service('provider-contact');
      const contacts: IContactApi = await service.find({
        query: {
          providerId,
        },
      });

      this.all = contacts.data.map((contact) => {
        return {
          id: contact.id,
          providerId: contact.providerId,
          firstName: contact.firstName || '',
          middleName: contact.middleName || '',
          lastName: contact.lastName || '',
          title: contact.title || '',
          officePhone: contact.officePhone || '',
          mobilePhone: contact.mobilePhone || '',
          fax: contact.fax || '',
          emailAddress: contact.emailAddress || '',
          default: contact.default ? true : false,
          createdAt: contact.createdAt,
          updatedAt: contact.updatedAt,
        };
      });
      this.loading = false;
    } catch (err) {
      this.loading = false;
      this.errorLoading = true;
      console.error(err);
      throw err;
    }
  };

  @action public startEditingContacts = (): void => {
    this.editing = true;
    this.creating = false;
    this.saving = false;
    this.errorSaving = EContactSavingError.None;
    this.editedContacts = this.combined.map((contact, i) => {
      return {
        id: contact.id,
        firstName: contact.firstName,
        middleName: contact.middleName,
        lastName: contact.lastName,
        title: contact.title,
        emailAddress: contact.emailAddress,
        officePhone: contact.officePhone,
        mobilePhone: contact.mobilePhone,
        fax: contact.fax,
        default: contact.default,
        modified: false,
      };
    });
  };

  @action public startCreatingNewContact = (): void => {
    this.creating = true;
    this.saving = false;
    this.errorSaving = EContactSavingError.None;
    if (this.createContact === null) {
      this.editedContacts.push({
        id: null,
        firstName: '',
        middleName: '',
        lastName: '',
        title: '',
        emailAddress: '',
        officePhone: '',
        mobilePhone: '',
        fax: '',
        default: this.all.length > 0 ? false : true,
        modified: false,
      });
    }
  };

  @action public toggleDefaultContact = (id: number, checked: boolean) => {
    const editingIndex = this.editedContactsIds.indexOf(id);
    if (editingIndex !== -1) {
      if (checked === false) {
        this.editedContacts[editingIndex].default = false;
        this.editedContacts[editingIndex].modified = true;
      } else {
        this.editedContacts.map((contact, i) => {
          if (editingIndex === i) {
            if (this.editedContacts[i].default !== true) {
              this.editedContacts[i].default = true;
              this.editedContacts[i].modified = true;
            }
          } else {
            if (this.editedContacts[i].default !== false) {
              this.editedContacts[i].default = false;
              this.editedContacts[i].modified = true;
            }
          }
        });
      }
    }
  };

  @action public cancelEditingContacts = (): void => {
    this.creating = false;
    this.editing = false;
    this.saving = false;
    this.errorSaving = EContactSavingError.None;
    this.editedContacts = [];
  };

  @action public cancelCreatingNewContact = (): void => {
    this.creating = false;
    if (this.createContact !== null) {
      const index = this.editedContactsIds.indexOf(null);
      if (index !== -1) {
        this.editedContacts = this.editedContacts
          .slice(0, index)
          .concat(this.editedContacts.slice(index + 1));
      }
    }
  };

  @action public saveEditedContacts = async (): Promise<any> => {
    try {
      this.saving = true;
      this.errorSaving = EContactSavingError.None;

      const service = await app.service('provider-contact');

      const results = await this.editedContacts.map(
        async (contact, i): Promise<any> => {
          if (contact.modified) {
            if (contact.id !== null && contact.firstName && contact.lastName) {
              const patch: IContact = await service.patch(contact.id, {
                firstName: contact.firstName.trim(),
                middleName: contact.middleName.trim(),
                lastName: contact.lastName.trim(),
                title: contact.title.trim(),
                officePhone: contact.officePhone.trim(),
                mobilePhone: contact.mobilePhone.trim(),
                fax: contact.fax.trim(),
                emailAddress: contact.emailAddress.trim(),
                default: contact.default,
              });

              const index = this.allIds.indexOf(contact.id);
              if (index !== -1) {
                this.all[index].firstName = contact.firstName.trim();
                this.all[index].middleName = contact.middleName.trim();
                this.all[index].lastName = contact.lastName.trim();
                this.all[index].title = contact.title.trim();
                this.all[index].officePhone = contact.officePhone.trim();
                this.all[index].mobilePhone = contact.mobilePhone.trim();
                this.all[index].fax = contact.fax.trim();
                this.all[index].emailAddress = contact.emailAddress.trim();
                this.all[index].default = contact.default;
              } else {
                throw new Error('Contact does not exist');
              }

              return patch;
            } else if (contact.id === null) {
              return null;
            } else {
              throw new Error('Name is required');
            }
          }
        }
      );

      this.editing = false;
      this.creating = false;
      this.errorSaving = EContactSavingError.None;
      this.editedContacts = [];

      return true;
    } catch (err) {
      this.errorSaving =
        typeof err.message !== 'undefined' && err.message === 'Name is required'
          ? EContactSavingError.NoName
          : EContactSavingError.Other;
      console.error(err);
      return false;
    } finally {
      this.saving = false;
    }
  };

  @action public saveNewContact = async (): Promise<any> => {
    try {
      if (
        this.providerId !== null &&
        this.createContact.firstName &&
        this.createContact.lastName
      ) {
        this.saving = true;
        this.errorSaving = EContactSavingError.None;

        const service = await app.service('provider-contact');
        const post: IContact = await service.create({
          providerId: this.providerId,
          firstName: this.createContact.firstName.trim(),
          middleName: this.createContact.middleName.trim(),
          lastName: this.createContact.lastName.trim(),
          title: this.createContact.title.trim(),
          officePhone: this.createContact.officePhone.trim(),
          mobilePhone: this.createContact.mobilePhone.trim(),
          fax: this.createContact.fax.trim(),
          emailAddress: this.createContact.emailAddress.trim(),
          default: this.createContact.default,
        });

        this.all.push(post);
        this.saving = false;
        this.creating = false;
        this.errorSaving = EContactSavingError.None;

        return true;
      } else {
        throw new Error('Name is required');
      }
    } catch (err) {
      this.saving = false;
      this.errorSaving =
        typeof err.message !== 'undefined' && err.message === 'Name is required'
          ? EContactSavingError.NoName
          : EContactSavingError.Other;
      console.error(err);
      return err;
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/LeadContacts.ts